import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence, useInView } from "framer-motion";
import { Waves as WavesIcon, Zap } from "lucide-react";
import { INDIVIDUAL_EVENT_COUNT } from "@/lib/brand";

/**
 * Nike-style auto-cycling showcase — the 5 strokes of swimming.
 * Background color shifts smoothly per stroke; bold typography slides in;
 * animated "ribbon" rotates like the Nike product.
 */
const STROKES = [
  {
    key: "freestyle",
    name: "FREESTYLE",
    tag: "The Pure Sprint",
    color: "#00F0FF",
    bg1: "#021F2F",
    bg2: "#0B1429",
    distances: ["50m", "100m", "200m", "400m"],
    blurb: "Four iconic distances from the explosive 50m sprint to the gruelling 400m. The discipline that defines every Masters swimmer.",
    img: "https://images.pexels.com/photos/863988/pexels-photo-863988.jpeg",
    isStroke: true,
  },
  {
    key: "backstroke",
    name: "BACKSTROKE",
    tag: "Eyes to the Sky",
    color: "#7DD3FC",
    bg1: "#0B1F3A",
    bg2: "#0B1429",
    distances: ["50m", "100m", "200m"],
    blurb: "Three precision distances. The only stroke raced face-up — where rhythm, sight markers, and reading the wall are everything.",
    img: "https://images.unsplash.com/photo-1530549387789-4c1017266635?w=1200",
    isStroke: true,
  },
  {
    key: "breaststroke",
    name: "BREASTSTROKE",
    tag: "Power & Glide",
    color: "#34D399",
    bg1: "#062E2A",
    bg2: "#0B1429",
    distances: ["50m", "100m", "200m"],
    blurb: "The oldest competitive stroke. Underwater pull-outs, perfect timing, and the iconic kick that separates good from great.",
    img: "https://images.pexels.com/photos/261185/pexels-photo-261185.jpeg",
    isStroke: true,
  },
  {
    key: "butterfly",
    name: "BUTTERFLY",
    tag: "Strength Embodied",
    color: "#FBBF24",
    bg1: "#3B1F00",
    bg2: "#0B1429",
    distances: ["50m", "100m", "200m"],
    blurb: "Both arms over the water, dolphin kick beneath. The most physically demanding stroke — and the most spectacular to watch.",
    img: "https://images.pexels.com/photos/73760/swimming-swimmer-female-race-73760.jpeg",
    isStroke: true,
  },
  {
    key: "im",
    name: "INDIVIDUAL MEDLEY",
    tag: "All Four. One Race.",
    color: "#F472B6",
    bg1: "#3B0A2E",
    bg2: "#0B1429",
    distances: ["200m", "400m"],
    blurb: "Not a stroke — a combination. Butterfly → Backstroke → Breaststroke → Freestyle in sequence. The ultimate Masters test of versatility.",
    img: "/images/medley.png",
    isStroke: false,
  },
];

export default function StrokeShowcase() {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const stroke = STROKES[idx];
  const sectionRef = useRef(null);
  // Continuous (infinite-repeat) animations below are expensive to keep
  // compositing while off-screen — that's what was causing mobile scroll
  // to feel stuck. Only run them while the section is actually in view.
  const inView = useInView(sectionRef, { margin: "-10% 0px -10% 0px" });

  useEffect(() => {
    if (paused) return;
    const id = setInterval(() => setIdx((p) => (p + 1) % STROKES.length), 4500);
    return () => clearInterval(id);
  }, [paused]);

  return (
    <section
      ref={sectionRef}
      data-testid="stroke-showcase"
      className="relative overflow-x-hidden py-16 md:py-24"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Animated background */}
      <motion.div
        className="absolute inset-0 -z-10"
        animate={{ background: `radial-gradient(ellipse at center, ${stroke.bg1} 0%, ${stroke.bg2} 70%)` }}
        transition={{ duration: 1.2, ease: "easeOut" }}
      />

      {/* Caustic light orbs that pulse — paused off-screen to keep scrolling smooth */}
      <motion.div
        className="absolute -top-32 -left-32 w-[40rem] h-[40rem] rounded-full pointer-events-none blur-[60px] md:blur-[120px]"
        animate={inView ? { backgroundColor: stroke.color, opacity: [0.08, 0.18, 0.08] } : { opacity: 0 }}
        transition={inView ? { duration: 4, repeat: Infinity, ease: "easeInOut" } : { duration: 0.4 }}
      />
      <motion.div
        className="absolute -bottom-32 -right-32 w-[35rem] h-[35rem] rounded-full pointer-events-none blur-[60px] md:blur-[120px]"
        animate={inView ? { backgroundColor: stroke.color, opacity: [0.1, 0.2, 0.1] } : { opacity: 0 }}
        transition={inView ? { duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 } : { duration: 0.4 }}
      />

      <div className="max-w-7xl mx-auto px-6 lg:px-10 relative">
        <div className="text-center mb-10">
          <div className="text-xs font-semibold text-[#94A3B8] mb-3">4 Strokes · 1 Medley · {INDIVIDUAL_EVENT_COUNT} Individual Events</div>
          <h2 className="font-display font-black text-4xl md:text-6xl">
            Pick your <span className="text-[#00F0FF]">water</span>.
          </h2>
        </div>

        {/* Main showcase */}
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-10 items-center">
          {/* Left: Text */}
          <div className="lg:col-span-5 relative min-h-0 lg:min-h-[360px]">
            <AnimatePresence mode="wait">
              <motion.div
                key={stroke.key}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
              >
                <div
                  className="text-xs uppercase tracking-[0.4em] mb-3 font-semibold"
                  style={{ color: stroke.color }}
                >
                  {stroke.tag}
                </div>
                <h3
                  className="font-display font-black text-[2rem] sm:text-4xl md:text-5xl lg:text-7xl leading-[0.88] tracking-tight"
                  style={{ color: "#FFFFFF" }}
                >
                  {stroke.name.split(" ").map((word, i) => (
                    <motion.span
                      key={i}
                      className="block"
                      initial={{ opacity: 0, x: -30 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.1 + i * 0.08 }}
                    >
                      {word}
                    </motion.span>
                  ))}
                </h3>
                <p className="text-[#CBD5E1] mt-4 sm:mt-6 text-sm sm:text-base md:text-lg leading-relaxed max-w-md">
                  {stroke.blurb}
                </p>

                <div className="flex flex-wrap gap-2 mt-7">
                  {stroke.distances.map((d, i) => (
                    <motion.span
                      key={d}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.4 + i * 0.05 }}
                      className="px-3 py-1.5 text-xs font-mono rounded-full border"
                      style={{ borderColor: stroke.color + "60", color: stroke.color }}
                    >
                      {d}
                    </motion.span>
                  ))}
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Right: Image with rotating ring */}
          <div className="lg:col-span-7 relative flex items-center justify-center">
            <div className="relative w-[240px] h-[240px] sm:w-[300px] sm:h-[300px] md:w-[480px] md:h-[480px]">
              {/* Rotating outer ring */}
              <motion.div
                className="absolute inset-0 rounded-full border-2"
                style={{ borderColor: stroke.color + "40" }}
                animate={inView ? { rotate: 360 } : {}}
                transition={{ duration: 24, repeat: Infinity, ease: "linear" }}
              >
                <span
                  className="absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-4 rounded-full"
                  style={{ background: stroke.color, boxShadow: `0 0 24px ${stroke.color}` }}
                />
              </motion.div>
              {/* Inner ring rotating opposite */}
              <motion.div
                className="absolute inset-6 rounded-full border"
                style={{ borderColor: stroke.color + "20" }}
                animate={inView ? { rotate: -360 } : {}}
                transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
              />
              {/* Image */}
              <AnimatePresence mode="wait">
                <motion.div
                  key={stroke.key}
                  initial={{ opacity: 0, scale: 0.85, rotate: -8 }}
                  animate={{ opacity: 1, scale: 1, rotate: 0 }}
                  exit={{ opacity: 0, scale: 0.85, rotate: 8 }}
                  transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                  className="absolute inset-10 rounded-full overflow-hidden"
                  style={{ boxShadow: `0 0 80px ${stroke.color}40` }}
                >
                  <img
                    src={stroke.img}
                    alt={stroke.name}
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                  <div
                    className="absolute inset-0"
                    style={{ background: `radial-gradient(circle at center, transparent 40%, ${stroke.bg1}90 100%)` }}
                  />
                </motion.div>
              </AnimatePresence>

              {/* Floating event count badge */}
              <motion.div
                key={stroke.key + "-badge"}
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.4, type: "spring" }}
                className="absolute -bottom-2 -right-2 md:bottom-4 md:right-4 bg-[#0B1429] border-2 rounded-full w-20 h-20 md:w-24 md:h-24 flex flex-col items-center justify-center"
                style={{ borderColor: stroke.color }}
              >
                <div className="font-display font-black text-3xl" style={{ color: stroke.color }}>
                  {stroke.distances.length}
                </div>
                <div className="text-[9px] text-[#94A3B8] uppercase tracking-wider">events</div>
              </motion.div>
            </div>
          </div>
        </div>

        {/* Pagination dots — 44px-tall tap target, thin visual bar centered inside */}
        <div className="flex justify-center mt-8" data-testid="stroke-dots">
          {STROKES.map((s, i) => (
            <button
              key={s.key}
              onClick={() => setIdx(i)}
              data-testid={`stroke-dot-${s.key}`}
              aria-label={s.name}
              className="relative w-11 h-11 flex items-center justify-center touch-manipulation"
            >
              <span
                className="relative block h-1.5 rounded-full overflow-hidden transition-all"
                style={{
                  width: i === idx ? 56 : 24,
                  background: i === idx ? "transparent" : "#2A3A5F",
                }}
              >
                {i === idx && (
                  <motion.span
                    key={stroke.key + "-bar"}
                    initial={{ width: "0%" }}
                    animate={{ width: paused ? "20%" : "100%" }}
                    transition={{ duration: paused ? 0 : 4.5, ease: "linear" }}
                    className="absolute inset-y-0 left-0 h-full"
                    style={{ background: stroke.color }}
                  />
                )}
              </span>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}
